import {
  Autocomplete,
  Box,
  Grid,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import ButtonComponent from "../../../Components/Common/button/Button";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import DownloadIcon from "@mui/icons-material/Download";
import { MessagePopup } from "../../../Components/Common/messagePopup/MessagePopup";
import { useMFBottlerSelection } from "../../../context/MFBottlerSelectionContext";
import ApiService from "../../../utils/fetch";
import { useSelector } from "react-redux";
import MFBottlerSelectionAccordion from "./MFBottlerSelectionAccordion";
import SettingsIcon from "@mui/icons-material/Settings";
import { useNavigate, useLocation, Link } from "react-router-dom";
import MFCustomBottlerSelectionAccordion from "./MFCustomBottlerSelectionAccordion";
import { useMFCustomBottlerSelection } from "../../../context/MFCustomBottlerSelectionContext";
import MFManualUpdate from "./MFManualUpdate";

const MFManageForecastMainContent = () => {
  const [forecastPeriod, setForecastPeriod] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { selectedData } = useMFBottlerSelection();
  const { customSelectedData } = useMFCustomBottlerSelection();

  const [activeTab, setActiveTab] = useState(0);
  const [customForecastPeriod, setCustomForecastPeriod] = useState([]);
  const [isBottlerUser, setIsBottlerUser] = useState(null);

  const [hasCustomBottlerSelection, setHasCustomBottlerSelection] =
    useState(false);
  const [hasStandardBottlerSelection, setHasStandardBottlerSelection] =
    useState(false);

  const [isCheckingConfiguration, setIsCheckingConfiguration] = useState(true);
  const [isCheckingStandardConfiguration, setIsCheckingStandardConfiguration] =
    useState(true);

  const [customSelectedPeriod, setCustomSelectedPeriod] = useState("");
  const [isCustomUploading, setIsCustomUploading] = useState(false);
  const fileCustomInputRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const roles = useSelector((state) => state.user.roles);

  useEffect(() => {
    if (roles) {
      setIsBottlerUser(roles.includes("Bottler"));
    }
  }, [roles]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await ApiService.get(
          "/v1/custom-market-forecast/configuration-bottler-selection",
        );

        const data = response?.data || [];

        setHasCustomBottlerSelection(Array.isArray(data) && data.length > 0);
      } catch (error) {
        console.error(error);
        setHasCustomBottlerSelection(false);
      } finally {
        setIsCheckingConfiguration(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await ApiService.get(
          "v1/manage-screen/market-forecast-bottler-selection",
        );

        const data = response?.data || [];

        setHasStandardBottlerSelection(Array.isArray(data) && data.length > 0);
      } catch (error) {
        console.error(error);
        setHasStandardBottlerSelection(false);
      } finally {
        setIsCheckingStandardConfiguration(false);
      }
    };

    fetchData();
  }, []);

  const mfManageForecastBottlerData = useSelector(
    (state) => state.MFManageScreenRedux.mfManageForecastBottlerData,
  );

  const isStandardTemplateDisabled = selectedData.length === 0;

  const mfCustomManageForecastBottlerData = useSelector(
    (state) =>
      state.MFCustomManageScreenRedux.mfCustomManageForecastBottlerData,
  );

  const isCustomTemplateDisabled = customSelectedData.length === 0;

  const mfCustomManageForecastTreeData = useSelector(
    (state) => state.MFCustomManageScreenRedux.mfCustomManageForecastTreeData,
  );

  const isEmptyCustomBottlerSelection =
    Array.isArray(mfCustomManageForecastTreeData) &&
    mfCustomManageForecastTreeData.length > 0;

  const cleanPeriod = (period) => period.replace(/-\w{3}$/, "");

  useEffect(() => {
    const fetchforecastPeriod = async () => {
      try {
        const response = await ApiService.get(
          "/v1/data-element/applicable-forecast-period",
        );
        if (response.status === 200 && Array.isArray(response.data)) {
          setForecastPeriod(response.data);
        }
      } catch (error) {
        console.error("Error fetching custom forecast periods:", error);
      }
    };
    fetchforecastPeriod();
  }, []);

  useEffect(() => {
    if (location.state?.activeTab !== undefined) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  const groupByCustomer = (data) => {
    const map = {};

    data.forEach(({ pcCountry, customerId }) => {
      if (!map[customerId]) {
        map[customerId] = {
          customerId,
          profitCenterCountry: [],
        };
      }

      map[customerId].profitCenterCountry.push(pcCountry);
    });

    return Object.values(map);
  };

  const getUniqueSelectedBottlers = (selectedData) => {
    return [...new Set(selectedData.map((item) => item.bottler))];
  };

  const getPlanningPartnerId = (bottler) => {
    if (!bottler) return "";

    const parts = bottler.split("-");
    return parts.length > 1 ? parts[1].trim() : "";
  };

  const handleFileDownload = async (e) => {
    e.preventDefault();

    const selectedBottlers = getUniqueSelectedBottlers(selectedData);

    if (selectedBottlers.length > 1) {
      MessagePopup({
        icon: "error",
        title: "Invalid Selection",
        message:
          "Multiple Bottlers selected. Please select Countries for only ONE Bottler.",
      });
      return;
    }

    if (!selectedPeriod || selectedData.length === 0) {
      MessagePopup({
        icon: "error",
        title: "Error!",
        message: "Please select Forecast Period, and Bottler Selection!",
      });
      return;
    }

    setLoading(true); // Start the loader

    console.log("Selected Data for Download:", selectedData);

    const payloadData = {
      fiscalMonthDescription: cleanPeriod(selectedPeriod),

      bottler: mfManageForecastBottlerData?.bottler ?? null,

      bottlerOwnerCode: mfManageForecastBottlerData?.bottlerOwnerCode ?? null,

      profitCenterCountryList: [
        ...new Set(selectedData.map((item) => item.profitCenterCountry)),
      ],
    };

    console.log("Payload Data for Download:", payloadData);

    try {
      const response = await ApiService.downloadFile(
        "/v1/market-forecast/generate-template",
        payloadData,
      );

      if (response.status === 200 && response.data) {
        // Create an object URL for the Blob
        const url = window.URL.createObjectURL(response.data);

        // Generate a timestamp for the filename (optional)
        const timestamp = new Date().toISOString().replace(/[-T:.Z]/g, "_");

        // Set the filename for the download
        const filename = `MarketForecast_${selectedPeriod}_${timestamp}1.xlsx`;

        // Create a temporary link to trigger the download
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);

        // Append the link to the document body, click it, then remove it
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the object URL to release memory
        URL.revokeObjectURL(url);

        MessagePopup({
          icon: "success",
          message: "Excel file downloaded successfully!",
        });
      }
    } catch (error) {
      console.error("Request Error:", error);

      const defaultMessage = "An error occurred while processing data.";
      let messageToShow = defaultMessage;

      try {
        const { response } = error;

        if (response) {
          const { status, data, headers } = response;
          const contentType = headers?.["content-type"] || "";

          if (data instanceof Blob) {
            const text = await data.text();

            if (contentType.includes("application/json")) {
              // Try parsing JSON error
              try {
                const json = JSON.parse(text);
                messageToShow = json.error || json.message || defaultMessage;
              } catch {
                messageToShow = text; // fallback to raw text
              }
            } else {
              messageToShow = text; // plain text or HTML error
            }
          } else if (status === 404) {
            messageToShow = "Requested resource not found (404).";
          }
        } else {
          // No response — likely a network or CORS error
          messageToShow = error.message || defaultMessage;
        }
      } catch (handlingError) {
        console.error("Error while handling the error:", handlingError);
        messageToShow = defaultMessage;
      }

      MessagePopup({
        icon: "error",
        title: "Error!",
        message: messageToShow,
      });
    } finally {
      setLoading(false); // Always stop loader
    }
  };

  const handleFileUpload = async (event) => {
    if (isUploading) return; // Prevent multiple clicks while loading

    if (!selectedPeriod || selectedData.length === 0) {
      MessagePopup({
        icon: "error",
        title: "Error!",
        message: "Please select Forecast Period, and Bottler Selection!",
      });
      return;
    }

    const selectedBottlers = getUniqueSelectedBottlers(selectedData);

    if (selectedBottlers.length > 1) {
      MessagePopup({
        icon: "error",
        title: "Invalid Selection",
        message:
          "Multiple Bottlers selected. Please select Countries for only ONE Bottler.",
      });
      return;
    }

    const selectedFile = event.target.files[0];
    if (!selectedFile) {
      MessagePopup({
        icon: "error",
        title: "Error!",
        message: "Please select a file!",
      });
      return;
    }

    const payloadData = {
      fiscalMonthDescription: cleanPeriod(selectedPeriod),

      bottler: mfManageForecastBottlerData?.bottler ?? null,

      bottlerOwnerCode: mfManageForecastBottlerData?.bottlerOwnerCode ?? null,

      profitCenterCountryList: [
        ...new Set(selectedData.map((item) => item.profitCenterCountry)),
      ],
    };

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("requestJson", JSON.stringify(payloadData));

    setIsUploading(true); // Start the loader

    MessagePopup({
      title: "Confirm Submission of Forecast",
      html: `<p>Are you sure you want to submit the <span class="red-text">${selectedPeriod} Forecast</span> for the selected combination ?</p>
             <p>File selected: ${selectedFile.name} </p>`,
      showCancelButton: true,
      confirmButtonText: "Ok",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#000000",
      cancelButtonColor: "#808080",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await ApiService.uploadAndDownload(
            "/v1/market-forecast/mf-upload",
            formData,
          );

          const validationResult = response.headers?.["x-validation-result"];

          if (validationResult === "SUCCESS") {
            MessagePopup({
              icon: "success",
              title: "Upload Completed",
              confirmButtonText: "Ok",
              confirmButtonColor: "#000000",
            });
          } else if (validationResult === "FAILED") {
            // Create an object URL for the Blob
            const url = window.URL.createObjectURL(response.data);

            // Generate a timestamp for the filename (optional)
            const timestamp = new Date().toISOString().replace(/[-T:.Z]/g, "_");

            // Set the filename for the download
            const filename = `MarketForecast_Sanity_ErrorReport_${selectedPeriod}_${timestamp}1.xlsx`;

            // Create a temporary link to trigger the download
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);

            // Append the link to the document body, click it, then remove it
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the object URL to release memory
            URL.revokeObjectURL(url);

            MessagePopup({
              icon: "error",
              title: "Upload Failed",
              html: `<p class="red-text"> Multiple errors found in the uploaded file. A report has been downloaded for your review </p>`,
              confirmButtonText: "Ok",
              confirmButtonColor: "#000000",
            });
          } else {
            MessagePopup({
              icon: "error",
              title: "Upload Failed",
              html: `<p class="red-text"> Error While Processing the Upload </p>`,
              confirmButtonText: "Ok",
              confirmButtonColor: "#000000",
            });
          }
        } catch (error) {
          console.log(error);
          let text = "Error While Processing the upload";
          if (error.response.data instanceof Blob) {
            text = await error.response.data.text();
          }
          MessagePopup({
            icon: "error",
            title: "Upload Failed",
            html: `<p class="red-text"> ${text} </p>`,
            confirmButtonText: "Ok",
            confirmButtonColor: "#000000",
          });
        } finally {
          setIsUploading(false); // Stop the loader
        }
      } else {
        setIsUploading(false); // Stop the loader if canceled
      }
    });
  };

  const handleCustomFileUpload = async (event) => {
    if (isCustomUploading) return; // Prevent multiple clicks while loading

    if (!customSelectedPeriod || customSelectedData.length === 0) {
      MessagePopup({
        icon: "error",
        title: "Error!",
        message: "Please select Forecast Period, and Bottler Selection!",
      });
      return;
    }

    const selectedBottlers = getUniqueSelectedBottlers(customSelectedData);

    if (selectedBottlers.length > 1) {
      MessagePopup({
        icon: "error",
        title: "Invalid Selection",
        message:
          "Multiple Bottlers selected. Please select Countries for only ONE Bottler.",
      });
      return;
    }

    const selectedFile = event.target.files[0];
    if (!selectedFile) {
      MessagePopup({
        icon: "error",
        title: "Error!",
        message: "Please select a file!",
      });
      return;
    }

    const payloadData = {
      fiscalMonthDescription: cleanPeriod(customSelectedPeriod),
      bottler: mfCustomManageForecastBottlerData?.bottler ?? null,
      bottlerOwnerCode:
        mfCustomManageForecastBottlerData?.bottlerOwnerCode ?? null,
      profitCenterCountryList: [
        ...new Set(customSelectedData.map((item) => item.profitCenterCountry)),
      ],
    };

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("requestJson", JSON.stringify(payloadData));

    setIsCustomUploading(true); // Start the loader

    MessagePopup({
      title: "Confirm Submission of Forecast",
      html: `<p>Are you sure you want to submit the <span class="red-text">${customSelectedPeriod} Forecast</span> for the selected combination ?</p>
             <p>File selected: ${selectedFile.name} </p>`,
      showCancelButton: true,
      confirmButtonText: "Ok",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#000000",
      cancelButtonColor: "#808080",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await ApiService.uploadAndDownload(
            "/v1/custom-market-forecast/template-upload",
            formData,
          );

          const validationResult = response.headers?.["x-validation-result"];

          if (validationResult === "SUCCESS") {
            MessagePopup({
              icon: "success",
              title: "Upload Completed",
              confirmButtonText: "Ok",
              confirmButtonColor: "#000000",
            });
          } else if (validationResult === "FAILED") {
            // Create an object URL for the Blob
            const url = window.URL.createObjectURL(response.data);

            // Generate a timestamp for the filename (optional)
            const timestamp = new Date().toISOString().replace(/[-T:.Z]/g, "_");

            // Set the filename for the download
            const filename = `Custom_MarketForecast_Sanity_ErrorReport_${selectedPeriod}_${timestamp}1.xlsx`;

            // Create a temporary link to trigger the download
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);

            // Append the link to the document body, click it, then remove it
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the object URL to release memory
            URL.revokeObjectURL(url);

            MessagePopup({
              icon: "error",
              title: "Upload Failed",
              html: `<p class="red-text"> Multiple errors found in the uploaded file. A report has been downloaded for your review </p>`,
              confirmButtonText: "Ok",
              confirmButtonColor: "#000000",
            });
          } else {
            MessagePopup({
              icon: "error",
              title: "Upload Failed",
              html: `<p class="red-text"> Error While Processing the Upload </p>`,
              confirmButtonText: "Ok",
              confirmButtonColor: "#000000",
            });
          }
        } catch (error) {
          console.log(error);
          let text = "Error While Processing the upload";
          if (error.response.data instanceof Blob) {
            const blobText = await error.response.data.text();

            try {
              const parsed = JSON.parse(blobText);
              text = parsed.message;
            } catch {
              text = blobText;
            }

            console.log(text, "textBlob");
          }
          MessagePopup({
            icon: "error",
            title: "Upload Failed",
            html: `<p class="red-text"> ${text} </p>`,
            confirmButtonText: "Ok",
            confirmButtonColor: "#000000",
          });
        } finally {
          setIsCustomUploading(false); // Stop the loader
        }
      } else {
        setIsCustomUploading(false); // Stop the loader if canceled
      }
    });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const templateConfiguration = () => {
    navigate("/bmf-custom-configuration");
  };

  return (
    <>
      <Stack spacing={1} sx={{ padding: "10px" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 500,
                minWidth: "200px",
              },
            }}
          >
            <Tab label="Standard Coke Template" />
            <Tab label="Custom Bottler Template" />
            <Tab label="Active BPP" />
          </Tabs>
        </Box>
        <Box role="tabpanel" hidden={activeTab !== 0}>
          <Grid container>
            <Grid item xs={8} spacing={1}>
              <Box sx={{ padding: "10px", borderRadius: "10px" }}>
                <Grid container spacing={3} mt={-1.75}>
                  <Grid item md={6} xs={12}>
                    <Autocomplete
                      disablePortal
                      options={forecastPeriod}
                      getOptionLabel={(option) => option}
                      size="small"
                      value={selectedPeriod}
                      onChange={(event, newValue) =>
                        setSelectedPeriod(newValue)
                      }
                      sx={{ width: "100%" }}
                      renderInput={(params) => (
                        <TextField {...params} label="Forecast Period *" />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Stack direction="row" spacing={2} mt={2} marginLeft={2}>
                <ButtonComponent
                  label={loading ? "Processing..." : "Download Template"}
                  bgColor="black"
                  color="white"
                  onClick={handleFileDownload}
                  disabled={loading || isStandardTemplateDisabled}
                  startIcon={<DownloadIcon />}
                />
                <ButtonComponent
                  label={isUploading ? "Uploading..." : "Upload Forecast"}
                  bgColor="black"
                  color="white"
                  onClick={() => {
                    if (!selectedPeriod || selectedData.length === 0) {
                      MessagePopup({
                        icon: "error",
                        title: "Error!",
                        message:
                          "Please select Forecast Term, Forecast Period, and Bottler Selection!",
                      });
                      return;
                    }
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                      fileInputRef.current.click();
                    }
                  }}
                  disabled={isUploading || isStandardTemplateDisabled}
                  startIcon={<FileUploadIcon />}
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileUpload}
                />
              </Stack>
              <Box sx={{ marginLeft: 2, marginTop: 5 }}>
                {!isCheckingStandardConfiguration &&
                  !hasStandardBottlerSelection &&
                  isBottlerUser !== null && (
                    <Typography
                      variant="body3"
                      color="red"
                      fontStyle="italic"
                      display="block"
                      mt={1}
                    >
                      {isBottlerUser ? (
                        "Please contact your CSR or Admin to setup the Standard Coke Template."
                      ) : (
                        <>
                          Complete the Template Setup in Bottler OnBoarding for
                          Standard Template.{" "}
                          <Link
                            to="/bottler-onboarding"
                            state={{ activeTab: "Bottler Market Forecast" }}
                            style={{
                              color: "black",
                              textDecoration: "underline",
                            }}
                          >
                            Bottler OnBoarding
                          </Link>
                        </>
                      )}
                    </Typography>
                  )}

                {!isCheckingStandardConfiguration && (
                  <Typography
                    variant="body3"
                    color="red"
                    fontStyle="italic"
                    display="block"
                  >
                    Make sure you upload the forecast file with only the
                    selected combinations.
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={4} marginTop={-2}>
              <Box sx={{ padding: "10px", borderRadius: "10px" }}>
                <Typography gutterBottom style={{ marginBottom: "-10px" }}>
                  Bottler Selection *
                </Typography>
                <MFBottlerSelectionAccordion></MFBottlerSelectionAccordion>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Box role="tabpanel" hidden={activeTab !== 1}>
          <Grid container>
            <Grid item xs={8} spacing={1}>
              <Box sx={{ padding: "10px", borderRadius: "10px" }}>
                <Grid container spacing={3} mt={-1.75}>
                  <Grid item md={6} xs={12}>
                    <Autocomplete
                      disablePortal
                      options={forecastPeriod}
                      getOptionLabel={(option) => option}
                      size="small"
                      value={customSelectedPeriod}
                      onChange={(event, newValue) =>
                        setCustomSelectedPeriod(newValue)
                      }
                      sx={{ width: "100%" }}
                      renderInput={(params) => (
                        <TextField {...params} label="Forecast Period *" />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Stack direction="row" spacing={2} mt={2} marginLeft={2}>
                {!isCheckingConfiguration && (
                  <ButtonComponent
                    label={
                      isEmptyCustomBottlerSelection
                        ? "Change Configuration"
                        : "Configuration"
                    }
                    bgColor={!isEmptyCustomBottlerSelection ? "red" : "black"}
                    color={!isEmptyCustomBottlerSelection ? "black" : "white"}
                    onClick={templateConfiguration}
                    startIcon={<SettingsIcon />}
                    maxWidth="300px"
                    disabled={!hasCustomBottlerSelection}
                  />
                )}
                <ButtonComponent
                  label={isCustomUploading ? "Uploading..." : "Upload Forecast"}
                  bgColor="black"
                  color="white"
                  startIcon={<FileUploadIcon />}
                  onClick={() => {
                    if (!customSelectedPeriod) {
                      MessagePopup({
                        icon: "error",
                        title: "Error!",
                        message:
                          "Please select Forecast Period, Bottler Selection",
                      });
                      return;
                    }
                    if (fileCustomInputRef.current) {
                      fileCustomInputRef.current.value = "";
                      fileCustomInputRef.current.click();
                    }
                  }}
                  disabled={isCustomUploading || isCustomTemplateDisabled}
                />
                <input
                  type="file"
                  ref={fileCustomInputRef}
                  style={{ display: "none" }}
                  onChange={handleCustomFileUpload}
                />
              </Stack>
              <Box sx={{ marginLeft: 2, marginTop: 5 }}>
                {!isCheckingConfiguration &&
                  !hasCustomBottlerSelection &&
                  isBottlerUser !== null && (
                    <Typography
                      variant="body3"
                      color="red"
                      fontStyle="italic"
                      display="block"
                      mt={1}
                    >
                      {isBottlerUser ? (
                        "Please contact your CSR or Admin to setup the Custom Bottler Template."
                      ) : (
                        <>
                          Complete the Template Setup in Bottler OnBoarding for
                          Custom Template.{" "}
                          <Link
                            to="/bottler-onboarding"
                            state={{ activeTab: "Bottler Market Forecast" }}
                            style={{
                              color: "black",
                              textDecoration: "underline",
                            }}
                          >
                            Bottler OnBoarding
                          </Link>
                        </>
                      )}
                    </Typography>
                  )}

                {!isCheckingConfiguration && (
                  <Typography
                    variant="body3"
                    color="red"
                    fontStyle="italic"
                    display="block"
                  >
                    Make sure you upload the forecast file with only the
                    selected combinations.
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={4} marginTop={-2}>
              <Box sx={{ padding: "10px", borderRadius: "10px" }}>
                <MFCustomBottlerSelectionAccordion />
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Box role="tabpanel" hidden={activeTab !== 2}>
          <MFManualUpdate activeTab={activeTab} />
        </Box>
      </Stack>
    </>
  );
};

export default MFManageForecastMainContent;
